<style lang="scss" scoped>
@import "./styles/date-picker.scss";
</style>

<template>
  <v-layout row wrap>
    <v-flex xs12>
      <v-date-picker
        v-if="inline"
        v-model="date"
        @input="onPickedDate"
        :min="minDate"
        :max="maxDate"
        :disabled="disabled"
        :allowed-dates="allowedDates"
        :weekday-format="weekdayFormat"
        no-title
        full-width
      ></v-date-picker>
      <v-menu
        v-else
        :close-on-content-click="false"
        v-model="isCalendarVisible"
        :nudge-right="!enableOffsetX ? 40 : 0"
        lazy
        transition="scale-transition"
        offset-y
        bottom
        offset-overflow
        :offset-x="enableOffsetX"
        full-width
        min-width="290px"
        :disabled="disabled"
        class="date-picker-container date-field-wrapper"
      >
        <v-text-field
          :label="label"
          :solo="solo"
          slot="activator"
          :placeholder="placeholder"
          v-model="formattedDate"
          :rules="dateInputRules"
          append-icon="event"
          :disabled="disabled"
          @change="handleChange"
          @blur="onBlur"
          :readonly="readonly"
          :outline="isOutline"
          :single-line="isSingleLine"
          :error-messages="
            isPassportExpiryError
              ? 'Required'
              : customErrorMessage
              ? customErrorMessage
              : ''
          "
        />
        <v-date-picker
          v-model="date"
          @input="onPickedDate"
          :min="minDate"
          :max="maxDate"
          :disabled="disabled"
          :allowed-dates="allowedDates"
          :weekday-format="weekdayFormat"
        ></v-date-picker>
      </v-menu>
    </v-flex>
    <v-spacer></v-spacer>
  </v-layout>
</template>

<script>
import moment from "moment";
import { get } from "lodash";
//#region private methods
let formatDate = (date) => {
  if (!date || date === "Invalid date") return null;
  let parsedDate = new Date(date).toISOString().slice(0, 10);

  const [year, month, day] = parsedDate.split("-");
  return `${day}/${month}/${year}`;
};

let checkValidMaxDate = (component, date) => {
  // If no max date is set, return as valid.
  if (!component.maxDate) {
    return true;
  }
  // Date should be on or before max date.
  return !moment(date, component.dateFormat).isAfter(moment(component.maxDate));
};

let checkValidMinDate = (component, date) => {
  // If no min date is set, return as valid.
  if (!component.minDate) {
    return true;
  }
  // Date should be on or after min date.
  return !moment(date, component.dateFormat).isBefore(
    moment(component.minDate)
  );
};

//#endregion
export default {
  name: "DatePicker",
  props: {
    enableOffsetX: Boolean,
    label: String,
    placeholder: {
      type: String,
      default: "",
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    defaultDate: String,
    minDate: String,
    maxDate: String,
    disabled: Boolean,
    solo: {
      type: Boolean,
      default: false,
    },
    meta: Object,
    allowedDates: Function,
    dateRules: {
      type: String,
      default: "",
    },
    dateFormat: String,
    isOutline: Boolean,
    isPassportExpiryError: Boolean,
    emitFreeTextUpdateOnEmpty: Boolean,
    customErrorMessage: String,
    allowedNull: Boolean,
    isSingleLine: Boolean,
    weekdayFormat: {
      type: Function,
      default: null,
    },
    inline: {
      type: Boolean,
      default: false,
    },
  },
  watch: {
    date() {
      const component = this;
      component.date = formatDate(component.date) ? component.date : null;
      component.formattedDate = formatDate(component.date);
    },
    defaultDate(newVal) {
      if (
        (this.date == null && newVal != null) ||
        (this.date !== newVal && newVal !== null)
      ) {
        this.date = newVal;
        this.$emit("update-date", newVal);
      }
    },
    formattedDate(newVal) {
      if (this.checkValidDate(newVal)) {
        this.handleChange();
      } else {
        this.formattedDate = null;
        if (this.emitFreeTextUpdateOnEmpty) {
          this.$emit("freetext-update", null);
        }
      }
    },
  },
  methods: {
    onPickedDate: function () {
      const component = this;
      component.isCalendarVisible = false;
      component.formattedDate = formatDate(component.date);
      component.$emit("update-date", component.date, component.meta);
    },
    onBlur: function () {
      const component = this;
      if (component.checkValidDate(component.formattedDate)) {
        this.handleChange();
      } else {
        component.formattedDate = null;
      }
    },
    handleChange: function () {
      const component = this;
      component.$emit(
        "freetext-update",
        moment(component.formattedDate, component.dateFormat).format(
          "YYYY-MM-DD"
        ),
        component.meta
      );
    },
    checkValidDate: function (date) {
      const component = this;
      // check if date format is in DD/MM/YYYY format
      return moment(date, "DD/MM/YYYY", true).isValid() ||
        (component.allowedNull && !date)
        ? true
        : moment(date, "DD/MM/YYYY", true).isValid();
    },
  },
  data() {
    return {
      date: null,
      formattedDate: null,
      isCalendarVisible: false,
    };
  },
  created() {
    const component = this;
    if (component.defaultDate) {
      component.date = component.defaultDate;
      component.$emit("update-date", component.date, component.meta);
    }
  },
  computed: {
    rules() {
      const component = this;
      return {
        none: [],
        required: [(value) => !!value || "Required field."],
        validDate: [
          (value) =>
            component.checkValidDate(value) ||
            this.$t("$profile.invalid_date", {
              dateformat: component.dateFormat,
            }),
        ],
        validMaxDate: [
          (value) => {
            if (!component.checkValidDate(value)) {
              return this.$t("$profile.invalid_date", {
                dateformat: component.dateFormat,
              });
            } else {
              if (!checkValidMaxDate(component, value)) {
                return `${component.label} should not be beyond ${formatDate(
                  component.maxDate
                )}.`;
              }
            }
            return false;
          },
        ],
        validMinDate: [
          (value) => {
            if (!component.checkValidDate(value)) {
              return this.$t("$profile.invalid_date", {
                dateformat: component.dateFormat,
              });
            } else {
              if (!checkValidMinDate(component, value)) {
                return `${component.label} should not be before ${formatDate(
                  component.minDate
                )}.`;
              }
            }
            return false;
          },
        ],
        validExistDate: [
          (value) => {
            if (!value) {
              return false;
            }
            return (
              component.checkValidDate(value) ||
              this.$t("$profile.invalid_date", {
                dateformat: component.dateFormat,
              })
            );
          },
        ],
      };
    },
    //allow date input have multiple rules
    dateInputRules() {
      const component = this;
      if (!component.dateRules) {
        //use required rules as default like previous implementation
        return component.rules.required;
      }
      //map dateRules string to rules array
      const listRules = component.dateRules
        .split(",")
        .map((rule) => get(this.rules, `${rule.trim()}.0`))
        .filter((item) => item);
      return listRules;
    },
  },
};
</script>
